import { Capacitor } from '@capacitor/core';
import React, { useEffect, useLayoutEffect, useId, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { getTooltipPosition } from '../../utils/tooltipPosition';

const InfoTooltip = ({
  children,
  text,
  label = 'More information',
  ariaLabel,
  compact = false,
  className = '',
  tooltipClassName = '',
}) => {
  const tooltipContent = children ?? text;
  const accessibleLabel = ariaLabel || label;
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const popupRef = useRef(null);
  const closeTimer = useRef(null);
  const tooltipId = useId();
  const [placement, setPlacement] = useState(null);
  const [dark, setDark] = useState(false);
  const [fontFamily, setFontFamily] = useState('inherit');
  const cancelClose = () => window.clearTimeout(closeTimer.current);
  const openTooltip = () => { cancelClose(); setIsOpen(true); };
  const scheduleClose = () => { cancelClose(); closeTimer.current = window.setTimeout(() => setIsOpen(false), 150); };
  const isNative = Capacitor.isNativePlatform();

  useEffect(() => () => window.clearTimeout(closeTimer.current), []);

  useLayoutEffect(() => {
    if (!isOpen) return undefined;
    const update = () => {
      const wrapper = wrapperRef.current;
      const popup = popupRef.current;
      if (!wrapper || !popup) return;
      const viewport = window.visualViewport;
      setPlacement(getTooltipPosition(wrapper.getBoundingClientRect(), popup.getBoundingClientRect(), {
        left: viewport?.offsetLeft || 0, top: viewport?.offsetTop || 0,
        width: viewport?.width || window.innerWidth, height: viewport?.height || window.innerHeight,
      }));
      setDark(Boolean(wrapper.closest('.dark')));
      setFontFamily(window.getComputedStyle(wrapper).fontFamily);
    };
    update();
    const observer = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
    if (popupRef.current) observer?.observe(popupRef.current);
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    window.visualViewport?.addEventListener('resize', update);
    window.visualViewport?.addEventListener('scroll', update);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
      window.visualViewport?.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('scroll', update);
    };
  }, [isOpen, tooltipContent]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const handlePointerDown = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target) &&
        !popupRef.current?.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <span
      ref={wrapperRef}
      className={`relative inline-flex shrink-0 items-center ${className}`}
      onMouseEnter={isNative ? undefined : openTooltip}
      onMouseLeave={isNative ? undefined : scheduleClose}
    >
      <button
        type="button"
        aria-label={accessibleLabel}
        aria-expanded={isOpen}
        aria-describedby={isOpen ? tooltipId : undefined}
        onPointerDown={(event) => {
          event.stopPropagation();
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          if (isNative) setIsOpen((current) => !current);
          else openTooltip();
        }}
        onFocus={isNative ? undefined : openTooltip}
        onBlur={isNative ? undefined : (event) => {
          if (!wrapperRef.current?.contains(event.relatedTarget)) {
            scheduleClose();
          }
        }}
        className={`relative inline-flex shrink-0 items-center justify-center rounded-full border border-slate-400 bg-slate-100 text-slate-700 transition-colors hover:border-teal-600 hover:bg-teal-100 hover:text-teal-900 dark:border-slate-400 dark:bg-slate-700 dark:text-slate-100 dark:hover:border-teal-300 dark:hover:bg-teal-900 dark:hover:text-teal-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-800 ${
          compact
            ? 'h-5 w-5 after:absolute after:-inset-2 after:rounded-full'
            : isNative ? 'h-8 w-8 touch-manipulation' : 'h-6 w-6'
        }`}
      >
        <svg aria-hidden="true" viewBox="0 0 16 16" className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
          <path d="M8 7v4" />
          <path d="M8 4.5h.01" strokeWidth="2.5" />
        </svg>
      </button>

      {isOpen && createPortal(
        <span className={dark ? 'dark' : ''} style={{ fontFamily }}>
          <span
            ref={popupRef}
            id={tooltipId}
            role="tooltip"
            tabIndex={0}
            onFocus={cancelClose}
            onBlur={scheduleClose}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            style={{ ...placement, transition: 'none', visibility: placement ? 'visible' : 'hidden' }}
            className={`fixed z-[100] w-72 max-w-[calc(100vw-1.5rem)] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white p-3 text-left text-sm font-normal leading-relaxed text-gray-700 shadow-xl dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200 ${tooltipClassName}`}
          >
            <span className="flex items-start gap-3">
              <span className="min-w-0 flex-1">{tooltipContent}</span>
              {isNative && (
                <button
                  type="button"
                  aria-label="Close information"
                  className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-gray-100 text-lg text-gray-700 dark:bg-slate-800 dark:text-slate-200"
                  onClick={() => setIsOpen(false)}
                >×</button>
              )}
            </span>
          </span>
        </span>,
        wrapperRef.current?.closest('dialog') || document.body
      )}

    </span>
  );
};

export default InfoTooltip;

